import { Link, useLocation, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { MessageCircle, GraduationCap, Trophy, Swords, User, Inbox, Crown, LogOut, CalendarDays, MessagesSquare, Wallet } from 'lucide-react';
import { useAuth } from '@/hooks/useAuth';
import { useAdmin } from '@/hooks/useAdmin';
import { supabase } from '@/integrations/supabase/client';

const navItems = [
  { path: '/ask', icon: MessageCircle, label: 'Ask' },
  { path: '/certifications', icon: GraduationCap, label: 'Certify' },
  { path: '/transformation', icon: Swords, label: 'Arena' },
  { path: '/pricing', icon: Crown, label: 'Plans' },
  { path: '/profile', icon: User, label: 'Profile' },
];

export function Navigation() {
  const location = useLocation();
  const navigate = useNavigate();
  const { isAdmin } = useAdmin();
  const { isAuthenticated } = useAuth();

  const handleLogout = async () => {
    await supabase.auth.signOut();
    navigate('/login');
  };

  // Simplified workspace nav for signed-in stylists on the Ask page:
  // only Ask + Profile (Sign Out button is rendered separately below).
  const isWorkspace = isAuthenticated && (location.pathname === '/ask' || location.pathname === '/dashboard');

  const items = isWorkspace
    ? [
        { path: '/ask', icon: MessageCircle, label: 'Ask' },
        { path: '/books/budget', icon: Wallet, label: 'Budget' },
        { path: '/profile', icon: User, label: 'Profile' },
      ]
    : isAdmin
    ? [
        { path: '/dashboard/schedule', icon: CalendarDays, label: 'Schedule' },
        { path: '/dashboard/messages', icon: MessagesSquare, label: 'Msgs' },
        { path: '/books/budget', icon: Wallet, label: 'Budget' },
        ...navItems,
        { path: '/admin/inbox', icon: Inbox, label: 'Inbox' },
      ]
    : navItems;

  return (
    <nav
      className="fixed bottom-0 left-0 right-0 z-50 border-t border-border bg-background/95 backdrop-blur-lg"
      style={{ paddingBottom: 'env(safe-area-inset-bottom, 0px)' }}
    >
      <div className="mx-auto flex max-w-md items-center justify-around py-2 px-1">
        {items.map(({ path, icon: Icon, label }) => {
          const isActive = location.pathname === path;
          return (
            <Link
              key={path}
              to={path}
              className="relative flex flex-col items-center gap-0.5 px-3 py-2 min-w-0"
              style={{ WebkitTapHighlightColor: 'transparent' }}
            >
              <div className={`rounded-xl p-1.5 transition-colors duration-200 ${isActive ? 'bg-foreground' : 'bg-transparent'}`}>
                <Icon
                  size={18}
                  className={`transition-colors duration-200 ${isActive ? 'text-background' : 'text-muted-foreground'}`}
                />
              </div>
              <span
                className={`text-[10px] font-body font-medium transition-colors duration-200 ${
                  isActive ? 'text-foreground' : 'text-muted-foreground'
                }`}
              >
                {label}
              </span>
              {isActive && (
                <motion.div
                  layoutId="nav-indicator"
                  className="absolute -top-px left-1/2 h-0.5 w-6 -translate-x-1/2 rounded-full bg-foreground"
                  transition={{ type: 'spring', stiffness: 400, damping: 30 }}
                />
              )}
            </Link>
          );
        })}
          {/* Logout */}
          <button
            onClick={handleLogout}
            className="relative flex flex-col items-center gap-0.5 px-3 py-2 min-w-0"
            style={{ WebkitTapHighlightColor: 'transparent' }}
          >
            <div className="rounded-xl p-1.5 bg-transparent">
              <LogOut size={18} className="text-muted-foreground" />
            </div>
            <span className="text-[10px] font-body font-medium text-muted-foreground">
              Out
            </span>
          </button>
      </div>
    </nav>
  );
}
